import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import get from 'lodash/get';
import isString from 'lodash/isString';
import React from 'react';

import Carousel from '@/components/Carousel';
import { ImageCaption } from '@/components/ImageCaption';
import { SectionTitle } from '@/components/SectionTitle';

const ONLY_SHOW_SAME_CONFIGURATION_FOR = ['Stripe', 'Monster'];

const UGCSlice: React.FC<{
  data: Queries.UGCSliceQuery;
  sliceContext: { title: string };
  configuration: string;
}> = ({ data, sliceContext, configuration }): JSX.Element => {
  const posts = data.ugc.nodes.filter((post) => {
    if (ONLY_SHOW_SAME_CONFIGURATION_FOR.includes(sliceContext.title)) {
      return post.variantsInPhoto.some((variant) => {
        // We split the configuration string by space and take the first
        // word so that we get "Rug" and not "Rug Medium", for example
        // prettier-ignore
        const variantConfig = variant.airtable.product.configuration.split(' ')[0];

        return configuration.includes(variantConfig);
      });
    }

    return post;
  });

  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="mb-8 md:mb-16">
      <div className="mx-4 mb-4 md:mx-8">
        <SectionTitle
          titleTag="h3"
          border={true}
          title={`${sliceContext.title} in situ`}
        />
      </div>

      <Carousel
        slideWidth="300px"
        startIndex={Math.floor((posts.length - 1) / 2)}
        showThumbnails={false}
        alignItems="start"
        slides={posts.map(({ username, photo }) => {
          const needsCredit =
            isString(photo.description) &&
            get(photo, 'metadata.tags', []).some(
              (tag) => tag.contentful_id === 'needsCredit',
            );

          return (
            <figure key={username}>
              <div>
                <GatsbyImage
                  alt={
                    needsCredit
                      ? `Shared by @${username}. ${photo.description}`
                      : `Shared by @${username}`
                  }
                  image={photo.gatsbyImageData}
                />
              </div>
              <ImageCaption
                caption={
                  needsCredit
                    ? `Shared by @${username}  
                   ${photo.description}`
                    : `Shared by @${username}`
                }
              />
            </figure>
          );
        })}
      />
    </section>
  );
};

export default UGCSlice;

export const query = graphql`
  query UGCSlice($title: String) {
    ugc: allContentfulInstagram(
      filter: {
        username: { ne: null }
        photo: { id: { ne: null } }
        variantsInPhoto: { elemMatch: { productName: { eq: $title } } }
      }
    ) {
      nodes {
        username
        photo {
          gatsbyImageData(
            formats: [AUTO, AVIF, WEBP]
            quality: 80
            width: 300
            height: 400
            cornerRadius: 2
            placeholder: DOMINANT_COLOR
            resizingBehavior: FILL
          )
          description
          metadata {
            tags {
              contentful_id
            }
          }
        }
        variantsInPhoto {
          airtable {
            product {
              configuration
            }
          }
        }
      }
    }
  }
`;
